import Phaser from 'phaser';
import type { Equipment, EquipmentSlot, ItemRarity } from '@roe/shared';
import { setupCamera } from '../dpr.js';
import { createHudTooltip, createItemTooltip } from '../utils/tooltip';
import { buildEquipStatusAccent, formatEquipmentTooltipLines, getArmorTypeLabel, getWeaponTypeLabel } from '../utils/heroEquip';

/** Format a stat bonus value with at most 2 decimal places — prevents float noise */
function fmtStatVal(v: number): string {
  if (v < 1) return `+${(Math.round(v * 1000) / 10).toFixed(1)}%`;
  return `+${Math.round(v * 100) / 100}`;
}

const STAT_LABELS: Record<string, string> = {
  hp: 'HP', mp: 'Mana', attack: 'ATK', defense: 'DEF',
  speed: 'SPD', critChance: 'Crit', critDamage: 'CritDmg',
  spellPower: 'SP',
  fireResist: '🔥 Res', frostResist: '❄️ Res', shadowResist: '🌑 Res', natureResist: '🌿 Res',
  // Pct-suffixed affix keys — human-readable, no "pct" string in the UI.
  // Value side already appends the %, so the label just names the stat.
  atkPct: 'ATK', spellPowerPct: 'SP', critDmgPct: 'CritDmg', attackSpeedPct: 'Attack Speed',
  defPct: 'DEF', hpPct: 'HP', maxMpPct: 'Max MP', mpRegenPct: 'MP Regen',
  goldPct: 'Gold', lootPct: 'Loot', xpPct: 'XP', ignoreArmorPct: 'Armor Pen',
  blockPct: 'Block', cdrPct: 'Cooldown',
  vsBeastPct: 'vs Beast', vsDemonPct: 'vs Demon', vsUndeadPct: 'vs Undead',
  vsOrcPct: 'vs Orc', vsDragonPct: 'vs Dragon', vsMinotaurPct: 'vs Minotaur',
  vsVoidPct: 'vs Void', vsBossPct: 'vs Boss',
};
/** Readable stat label. Any stat key not in STAT_LABELS and ending in "Pct"
 *  (a future stat we forgot to add) auto-strips the suffix rather than leaking
 *  "pct" into the tooltip. */
function fmtStatKey(k: string): string {
  if (STAT_LABELS[k]) return STAT_LABELS[k];
  if (k.endsWith('Pct')) {
    const root = k.slice(0, -3);
    return root.charAt(0).toUpperCase() + root.slice(1);
  }
  return k;
}
import { RARITY_COLORS, HERO_CLASS_NAMES, WEAPON_CLASS_RESTRICTIONS, ARMOR_CLASS_RESTRICTIONS, formatAffix, getRelicScaledEffect, aggregateBoonEffects } from '@roe/shared';
import { GameStateManager } from '../state/GameStateManager';
import { PhaserTextInput } from '../utils/PhaserTextInput';

export class InventoryScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private battleScene!: Phaser.Scene & { resumeBattle(): void };
  private contentGroup!: Phaser.GameObjects.Group;
  private scrollOffset = 0;
  private sortBy: 'rarity' | 'level' | 'slot' = 'level';
  private selectedItem: Equipment | null = null;
  /** When set, only show items for this slot */
  private filterSlot: EquipmentSlot | null = null;
  /** Tab mode — 'items' shows equipment, 'sigils' shows the per-run sigil bag */
  private filterMode: 'items' | 'sigils' = 'items';
  /** Active stat filters — item must have ALL selected stats (AND logic) */
  private statFilters: Set<string> = new Set();
  private itemTooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
  private inventorySearch = '';
  private searchInput: PhaserTextInput | null = null;
  /** Drag scrolling state */
  private isDragging = false;
  /** Scroll offset for the right-column comparison panel */
  private rightScrollOffset = 0;
  /** Max scroll for the right-column comparison panel (set each render) */
  private rightScrollMax = 0;
  /** Two-step recycle gate: legendary "Recycle All" button stays hidden until the
   *  player has clicked the epic "Recycle All" button at least once this session.
   *  Prevents accidental bulk-shredding of legendaries before the player has dealt
   *  with their cheaper epic stash. Reset on scene init. */
  private epicRecycleClicked = false;

  constructor() {
    super({ key: 'InventoryScene' });
  }

  init(data: { gsm: GameStateManager; battleScene: any }): void {
    this.gsm = data.gsm;
    this.battleScene = data.battleScene;
    this.scrollOffset = 0;
    this.rightScrollOffset = 0;
    this.rightScrollMax = 0;
    this.selectedItem = null;
    this.sortBy = 'level';
    this.filterSlot = null;
    this.filterMode = 'items';
    this.statFilters = new Set();
    this.inventorySearch = '';
    this.epicRecycleClicked = false;
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'overlay');
    this.cameras.main.setBackgroundColor('rgba(5, 3, 5, 0.97)');
    this.contentGroup = this.add.group();

    // Clear new loot badge
    this.gsm.player.newLootCount = 0;

    const W = 480;

    // ── Header ──
    this.add.rectangle(W / 2, 22, W, 44, 0x0a0806, 1).setDepth(10);
    this.add.rectangle(W / 2, 44, W, 1, 0x5a3600, 0.9).setDepth(10);
    this.add.text(W / 2, 20, '✦  Inventory  ✦', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    // Close button — ESC key mirrors it.
    const closeBtn = this.add.text(W - 20, 20, '✕', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(11);
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    closeBtn.on('pointerdown', () => this.closeScene());
    this.input.keyboard?.once('keydown-ESC', () => this.closeScene());

    // Direct nav back to Heroes — back-arrow glyph per user feedback ("HER"
    // wasn't readable as a nav action). Hover tooltip names the destination.
    const goHeroBg = this.add.rectangle(W - 60, 20, 40, 22, 0x140e06, 0.9)
      .setStrokeStyle(1, 0x5a3600).setDepth(11).setInteractive({ useHandCursor: true });
    const goHeroBtn = this.add.text(W - 60, 19, '←', {
      fontSize: '18px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(12);
    const onHeroOver = () => { goHeroBtn.setColor('#fbbf24'); goHeroBg.setStrokeStyle(1, 0x9a6020); };
    const onHeroOut  = () => { goHeroBtn.setColor('#c8973a'); goHeroBg.setStrokeStyle(1, 0x5a3600); };
    goHeroBg.on('pointerover', onHeroOver).on('pointerout', onHeroOut);
    goHeroBtn.on('pointerover', onHeroOver).on('pointerout', onHeroOut);
    const doGoHero = () => {
      this.gsm.save();
      this.scene.stop();
      this.scene.launch('HeroScene', { gsm: this.gsm, battleScene: this.battleScene });
    };
    goHeroBg.on('pointerdown', doGoHero);
    goHeroBtn.on('pointerdown', doGoHero);
    let goHeroTip: Phaser.GameObjects.Text | null = null;
    const showHeroTip = () => {
      goHeroTip?.destroy();
      goHeroTip = this.add.text(W - 60, 38, '← Heroes', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
        backgroundColor: '#0d0a0e', padding: { x: 4, y: 2 },
      }).setOrigin(0.5, 0).setDepth(20);
    };
    const hideHeroTip = () => { goHeroTip?.destroy(); goHeroTip = null; };
    goHeroBg.on('pointerover', showHeroTip).on('pointerout', hideHeroTip);
    goHeroBtn.on('pointerover', showHeroTip).on('pointerout', hideHeroTip);

    // Mouse wheel scroll — right side (x>230) scrolls comparison panel; left side scrolls item list
    this.input.on('wheel', (ptr: Phaser.Input.Pointer, _gos: unknown, _dx: unknown, dy: number) => {
      const step = dy > 0 ? 4 : -4;
      if (this.selectedItem && ptr.x > 230) {
        this.rightScrollOffset = Phaser.Math.Clamp(this.rightScrollOffset + step, 0, this.rightScrollMax);
      } else {
        const total = this.filterMode === 'sigils'
          ? (this.gsm.player.sigilBag?.length ?? 0)
          : this.getFilteredItems().length;
        const page = this.filterMode === 'sigils' ? 10 : 14;
        const maxOffset = Math.max(0, total - page);
        this.scrollOffset = Phaser.Math.Clamp(this.scrollOffset + step, 0, maxOffset);
      }
      this.refreshContent();
    });

    // Touch/drag scrolling
    let dragStartY = 0;
    let dragScrollStart = 0;
    this.isDragging = false;
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y < 80) return;
      dragStartY = pointer.y;
      dragScrollStart = this.scrollOffset;
      this.isDragging = false;
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      const dy = dragStartY - pointer.y;
      if (Math.abs(dy) > 4) this.isDragging = true;
      if (this.isDragging) {
        const total = this.filterMode === 'sigils'
          ? (this.gsm.player.sigilBag?.length ?? 0)
          : this.getFilteredItems().length;
        const page = this.filterMode === 'sigils' ? 10 : 14;
        const rowPx = this.filterMode === 'sigils' ? 36 : 24;
        const maxOffset = Math.max(0, total - page);
        const scrollDelta = Math.round(dy / rowPx);
        this.scrollOffset = Phaser.Math.Clamp(dragScrollStart + scrollDelta, 0, maxOffset);
        this.refreshContent();
      }
    });

    this.events.once('shutdown', () => { this.searchInput?.destroy(); this.searchInput = null; });
    this.createSearchInput();
    this.refreshContent();
  }

  /** Phaser-native search input — replaces the old HTML overlay. */
  private createSearchInput(): void {
    this.searchInput?.destroy();
    const SEARCH_X = 238;
    const SEARCH_W = 100;
    const SEARCH_H = 16;
    const SEARCH_Y = 73 - Math.round(SEARCH_H / 2);
    // Debounce the live filter rebuild — see HeroScene's createSearchInput for
    // the rationale. Keeps fast typing snappy without losing keystrokes.
    let pending: ReturnType<typeof setTimeout> | null = null;
    this.searchInput = new PhaserTextInput(this, {
      x: SEARCH_X, y: SEARCH_Y, width: SEARCH_W, height: SEARCH_H,
      placeholder: 'Search…',
      initialValue: this.inventorySearch,
      fontSize: '10px',
      depth: 25,
      onChange: (v) => {
        this.inventorySearch = v.toLowerCase();
        this.scrollOffset = 0;
        if (pending) clearTimeout(pending);
        pending = setTimeout(() => { pending = null; this.refreshContent(); }, 60);
      },
    });
  }

  private refreshContent(): void {
    // Destroy any active tooltip before clearing — it lives outside the group
    this.itemTooltip?.destroy();
    this.itemTooltip = null;
    this.contentGroup.clear(true, true);
    const W = 480;

    // Item count and gold
    const infoText = this.add.text(10, 52, `Items: ${this.gsm.player.inventory.length}  🪙 ${this.gsm.player.resources.gold}  ◆ ${this.gsm.player.resources.loot}`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020',
    });
    this.contentGroup.add(infoText);

    // ── Slot filter buttons ──
    type TabKey = EquipmentSlot | 'sigils' | null;
    const slots: Array<{ label: string; key: TabKey }> = [
      { label: 'All', key: null },
      { label: 'Wpn', key: 'weapon1' },
      { label: 'Arm', key: 'armor' },
      { label: 'Rel', key: 'relic1' },
      { label: 'Tro', key: 'reserved' },
      { label: 'Sig', key: 'sigils' },
    ];
    slots.forEach((s, i) => {
      const isActive = s.key === 'sigils'
        ? this.filterMode === 'sigils'
        : this.filterMode === 'items' && this.filterSlot === s.key;
      const bx = 10 + i * 38;
      const by = 68;
      const fbg = this.add.rectangle(bx + 18, by + 5, 36, 14, isActive ? 0x1a0f06 : 0x0d0a08, 1)
        .setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600)
        .setInteractive({ useHandCursor: true });
      const ftxt = this.add.text(bx + 18, by + 5, s.label, {
        fontSize: '8px', fontFamily: 'Georgia, serif',
        color: isActive ? '#c8973a' : '#6b4020',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      this.contentGroup.add(fbg);
      this.contentGroup.add(ftxt);

      // New-sigil badge on the Sig tab — small red dot with count in the top-right corner
      if (s.key === 'sigils' && (this.gsm.player.newSigilCount ?? 0) > 0) {
        const count = this.gsm.player.newSigilCount ?? 0;
        const badgeX = bx + 36;
        const badgeY = by;
        const badgeBg = this.add.circle(badgeX, badgeY, 6, 0x8b2000, 1)
          .setStrokeStyle(1, 0xff6020);
        const badgeTxt = this.add.text(badgeX, badgeY, `${count}`, {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#fff0d0', fontStyle: 'bold',
        }).setOrigin(0.5).setResolution(2);
        this.contentGroup.add(badgeBg);
        this.contentGroup.add(badgeTxt);
      }

      const doFilter = () => {
        if (s.key === 'sigils') {
          this.filterMode = 'sigils';
          this.filterSlot = null;
          this.selectedItem = null;
          // Clear the new-sigil badge the moment the player opens the tab
          if ((this.gsm.player.newSigilCount ?? 0) > 0) {
            this.gsm.player.newSigilCount = 0;
            this.gsm.save();
          }
        } else {
          this.filterMode = 'items';
          this.filterSlot = s.key;
        }
        this.scrollOffset = 0;
        this.refreshContent();
      };
      fbg.on('pointerdown', doFilter);
      ftxt.on('pointerdown', doFilter);
      fbg.on('pointerover', () => { ftxt.setColor('#c8973a'); fbg.setStrokeStyle(1, 0xc8973a); });
      fbg.on('pointerout', () => { ftxt.setColor(isActive ? '#c8973a' : '#6b4020'); fbg.setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600); });
    });

    if (this.filterMode === 'sigils') {
      this.drawSigilList(106);
      return;
    }

    // ── Sort buttons ──
    const sorts: Array<{ label: string; key: 'rarity' | 'level' | 'slot' }> = [
      { label: 'Rarity', key: 'rarity' },
      { label: 'Level', key: 'level' },
      { label: 'Slot', key: 'slot' },
    ];
    sorts.forEach((s, i) => {
      const isActive = this.sortBy === s.key;
      const bx = 10 + i * 70;
      const by = 86;
      const sbg = this.add.rectangle(bx + 28, by + 5, 54, 14, isActive ? 0x1a0f06 : 0x0d0a08, 1)
        .setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600)
        .setInteractive({ useHandCursor: true });
      const stxt = this.add.text(bx + 28, by + 5, s.label, {
        fontSize: '8px', fontFamily: 'Georgia, serif',
        color: isActive ? '#c8973a' : '#6b4020',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      this.contentGroup.add(sbg);
      this.contentGroup.add(stxt);
      const doSort = () => { this.sortBy = s.key; this.scrollOffset = 0; this.refreshContent(); };
      sbg.on('pointerdown', doSort);
      stxt.on('pointerdown', doSort);
      sbg.on('pointerover', () => { stxt.setColor('#c8973a'); sbg.setStrokeStyle(1, 0xc8973a); });
      sbg.on('pointerout', () => { stxt.setColor(isActive ? '#c8973a' : '#6b4020'); sbg.setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600); });
    });

    // ── Stat filter buttons (same row as sort buttons, to the right) ──
    const statFilterDefs: Array<{ label: string; key: string; tooltip: string }> = [
      { label: 'Atk',  key: 'attack',     tooltip: 'Attack Damage' },
      { label: 'SP',   key: 'spellPower', tooltip: 'Spell Power' },
      { label: 'Mana', key: 'mp',         tooltip: 'Max Mana' },
      { label: 'Def',  key: 'defense',    tooltip: 'Defense / Armor' },
      { label: 'Crit', key: 'critChance', tooltip: 'Critical Hit Chance' },
      { label: 'CDmg', key: 'critDamage', tooltip: 'Critical Hit Damage' },
    ];
    const sfBtnW = 38;
    const sfGap = 5;
    const sfStartCX = 232; // first button center (right of Slot button)
    statFilterDefs.forEach((sf, i) => {
      const isActive = this.statFilters.has(sf.key);
      const cx = sfStartCX + i * (sfBtnW + sfGap);
      const by = 86;
      const sfbg = this.add.rectangle(cx, by + 5, sfBtnW, 14, isActive ? 0x1a0f06 : 0x0d0a08, 1)
        .setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600)
        .setInteractive({ useHandCursor: true });
      const sftxt = this.add.text(cx, by + 5, sf.label, {
        fontSize: '8px', fontFamily: 'Georgia, serif',
        color: isActive ? '#c8973a' : '#6b4020',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      this.contentGroup.add(sfbg);
      this.contentGroup.add(sftxt);
      const doToggle = () => {
        if (this.statFilters.has(sf.key)) this.statFilters.delete(sf.key);
        else this.statFilters.add(sf.key);
        this.scrollOffset = 0;
        this.refreshContent();
      };
      sfbg.on('pointerdown', doToggle);
      sftxt.on('pointerdown', doToggle);
      let sfTip: Phaser.GameObjects.Text | null = null;
      const showTip = () => {
        sftxt.setColor('#c8973a');
        sfbg.setStrokeStyle(1, 0xc8973a);
        sfTip = this.add.text(cx, by - 2, sf.tooltip, {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#c8973a',
          backgroundColor: '#0d0a08', padding: { x: 4, y: 2 },
        }).setResolution(2).setOrigin(0.5, 1).setDepth(50);
        this.contentGroup.add(sfTip);
      };
      const hideTip = () => {
        sftxt.setColor(isActive ? '#c8973a' : '#6b4020');
        sfbg.setStrokeStyle(1, isActive ? 0xc8973a : 0x5a3600);
        sfTip?.destroy();
        sfTip = null;
      };
      sfbg.on('pointerover', showTip).on('pointerout', hideTip);
      sftxt.on('pointerover', showTip).on('pointerout', hideTip);
    });

    const items = this.getFilteredItems();

    if (this.selectedItem) {
      const visible = items.slice(this.scrollOffset, this.scrollOffset + 7);
      this.drawItemList(visible, 106);
      this.drawItemDetail(this.selectedItem);
    } else {
      const visible = items.slice(this.scrollOffset, this.scrollOffset + 14);
      this.drawItemList(visible, 106);
    }

    // ── Batch sell (left side — original behaviour) ──
    const rarityTiers: ItemRarity[] = ['common', 'uncommon', 'rare', 'epic'];
    const batchRarity = rarityTiers.find((r) => this.gsm.player.inventory.some((i) => i.rarity === r));
    if (batchRarity) {
      const RARITY_LABEL: Record<string, string> = { common: 'Common', uncommon: 'Uncommon', rare: 'Rare', epic: 'Epic' };
      const RARITY_BORDER: Record<string, number> = { common: 0x5a3600, uncommon: 0x3a7a30, rare: 0x1e40af, epic: 0x6b21a8 };
      const label = RARITY_LABEL[batchRarity] || batchRarity;
      const borderCol = RARITY_BORDER[batchRarity] || 0x5a3600;
      const batchItems = this.gsm.player.inventory.filter((i) => i.rarity === batchRarity);

      const sellBg = this.add.rectangle(90, 554, 160, 22, 0x0d0a08, 1)
        .setStrokeStyle(1, borderCol).setInteractive({ useHandCursor: true });
      const sellBtn = this.add.text(90, 554, `Sell All ${label} (${batchItems.length})`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      this.contentGroup.add(sellBg);
      this.contentGroup.add(sellBtn);
      sellBg.on('pointerover', () => { sellBtn.setColor('#c8973a'); sellBg.setStrokeStyle(1, 0xc8973a); });
      sellBg.on('pointerout', () => { sellBtn.setColor('#a07840'); sellBg.setStrokeStyle(1, borderCol); });
      const doSell = () => {
        this.showConfirm(`Sell ${batchItems.length} ${label.toLowerCase()} items?`, () => {
          let totalGold = 0;
          for (const item of batchItems) totalGold += this.gsm.sellItem(item.id);
          if (totalGold > 0) this.showMessage(`Sold ${batchItems.length} items for ${totalGold}🪙`);
          this.selectedItem = null;
          this.refreshContent();
        });
      };
      sellBg.on('pointerdown', doSell);
      sellBtn.on('pointerdown', doSell);

      // Lucky Break boon — batch recycle blocked too. Skip the whole button path.
      if (batchRarity === 'epic' && !aggregateBoonEffects(this.gsm.player.activeBoons).cannotRecycle) {
        const totalTokens = batchItems.length;
        const recBg = this.add.rectangle(250, 554, 140, 22, 0x0d0a08, 1)
          .setStrokeStyle(1, 0x1e40af).setInteractive({ useHandCursor: true });
        const recBtn = this.add.text(250, 554, `Recycle All Epic (${totalTokens}◆)`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });
        this.contentGroup.add(recBg);
        this.contentGroup.add(recBtn);
        recBg.on('pointerover', () => { recBtn.setColor('#c8973a'); recBg.setStrokeStyle(1, 0xb07820); });
        recBg.on('pointerout', () => { recBtn.setColor('#a07840'); recBg.setStrokeStyle(1, 0x1e40af); });
        const doRecycle = () => {
          // Two-step gate: clicking the epic button this session unlocks the
          // legendary button on next refresh. Flag flips on click (not on
          // confirm-yes) — the intent is "you saw the epic option first",
          // even if the player cancels the confirm.
          this.epicRecycleClicked = true;
          this.showConfirm(`Recycle ${batchItems.length} epic items for ${totalTokens}◆?`, () => {
            let totalMats = 0;
            for (const item of batchItems) totalMats += this.gsm.recycleItem(item.id);
            if (totalMats > 0) this.showMessage(`Recycled for ${totalMats}◆`);
            this.selectedItem = null;
            this.refreshContent();
          });
          // Refresh now so the legendary button appears even if the user
          // cancels the confirm — they've seen the epic option, gate is open.
          this.refreshContent();
        };
        recBg.on('pointerdown', doRecycle);
        recBtn.on('pointerdown', doRecycle);
      }
    }

    // ── Recycle All Legendary — gated behind clicking the Epic button this
    // session (see epicRecycleClicked). If the player has zero epics in the
    // bag, the gate is bypassed automatically since they can't click epic.
    // Sits to the right of the Epic button on the same y=554 row.
    if (!aggregateBoonEffects(this.gsm.player.activeBoons).cannotRecycle) {
      const legendaryItems = this.gsm.player.inventory.filter((i) => i.rarity === 'legendary');
      const hasAnyEpic = this.gsm.player.inventory.some((i) => i.rarity === 'epic');
      const legendaryUnlocked = this.epicRecycleClicked || !hasAnyEpic;
      if (legendaryItems.length > 0 && legendaryUnlocked) {
        const TOKENS_PER_LEGENDARY = 2; // matches recycleItem rate (line 685: legendary → 2◆)
        const totalTokens = legendaryItems.length * TOKENS_PER_LEGENDARY;
        const lrY = 554;
        const lrBg = this.add.rectangle(400, lrY, 140, 22, 0x0d0a08, 1)
          .setStrokeStyle(1, 0xf97316).setInteractive({ useHandCursor: true });
        const lrBtn = this.add.text(400, lrY, `Recycle All Legendary (${totalTokens}◆)`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#f97316', fontStyle: 'bold',
        }).setOrigin(0.5).setInteractive({ useHandCursor: true });
        this.contentGroup.add(lrBg);
        this.contentGroup.add(lrBtn);
        lrBg.on('pointerover', () => { lrBtn.setColor('#ffb060'); lrBg.setStrokeStyle(1, 0xffb060); });
        lrBg.on('pointerout', () => { lrBtn.setColor('#f97316'); lrBg.setStrokeStyle(1, 0xf97316); });
        const doLegendaryRecycle = () => {
          this.showConfirm(`Recycle ${legendaryItems.length} legendary items for ${totalTokens}◆?`, () => {
            let totalMats = 0;
            // Skip locked relics — `lockedRelics` blocks sell/recycle on those specific ids.
            const locked = new Set(this.gsm.player.lockedRelics ?? []);
            for (const item of legendaryItems) {
              if (locked.has(item.id)) continue;
              totalMats += this.gsm.recycleItem(item.id);
            }
            if (totalMats > 0) this.showMessage(`Recycled for ${totalMats}◆`);
            this.selectedItem = null;
            this.refreshContent();
          });
        };
        lrBg.on('pointerdown', doLegendaryRecycle);
        lrBtn.on('pointerdown', doLegendaryRecycle);
      }
    }

    // ── Auto-sell toggles (bottom-right) — checkboxes with tooltip ──
    const autoToggleDefs: { key: 'autoSellCommon' | 'autoSellUncommon' | 'autoSellRare'; label: string; tooltip: string; color: string }[] = [
      { key: 'autoSellCommon',   label: 'C', tooltip: 'Auto-sell Common\nItems of this rarity are sold\nautomatically when found.',   color: '#9ca3af' },
      { key: 'autoSellUncommon', label: 'U', tooltip: 'Auto-sell Uncommon\nItems of this rarity are sold\nautomatically when found.', color: '#22c55e' },
      { key: 'autoSellRare',     label: 'R', tooltip: 'Auto-sell Rare\nItems of this rarity are sold\nautomatically when found.',    color: '#3b82f6' },
    ];
    autoToggleDefs.forEach((def, i) => {
      // Moved to y=528 (above the recycle row) so the y=554 row can hold
      // sell + recycle-epic + recycle-legendary side by side without overlap.
      const tx = 410 + i * 26;
      const ty = 528;
      const isOn = !!this.gsm.player[def.key];
      // Background box. Bumped border weight to 2 px when ON for a clearer
      // active state. Per user feedback: the previous OFF-state label was in
      // a near-black `#3a2810` colour that read as "no label rendered" against
      // the matching `#0a0a0a` fill — looked like a broken empty box. New
      // off-colour brightens to `#7a5a30` so the rarity letter (C/U/R) is
      // visibly there even when the toggle is off, and the on-state contrast
      // remains stronger than before.
      const onBorderColor = Phaser.Display.Color.HexStringToColor(def.color).color;
      const bg = this.add.rectangle(tx, ty, 22, 22, isOn ? 0x0a1428 : 0x12100a, 1)
        .setStrokeStyle(isOn ? 2 : 1.5, isOn ? onBorderColor : 0x5a3a18)
        .setInteractive({ useHandCursor: true });
      this.contentGroup.add(bg);
      const checkTxt = this.add.text(tx, ty, isOn ? '✓' : def.label, {
        fontSize: isOn ? '14px' : '11px', fontFamily: 'Georgia, serif',
        color: isOn ? def.color : '#7a5a30', fontStyle: 'bold',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      this.contentGroup.add(checkTxt);

      const rarityMap: Record<string, string> = { autoSellCommon: 'common', autoSellUncommon: 'uncommon', autoSellRare: 'rare' };
      const doToggle = () => {
        this.gsm.player[def.key] = !this.gsm.player[def.key];
        this.gsm.save();
        if (this.gsm.player[def.key]) {
          const rarity = rarityMap[def.key];
          const toSell = this.gsm.player.inventory.filter((it) => it.rarity === rarity);
          if (toSell.length > 0) {
            let gold = 0;
            for (const it of toSell) gold += this.gsm.sellItem(it.id);
            if (gold > 0) this.showMessage(`Auto-sold ${toSell.length} ${rarity} items for 🪙${gold}`);
            this.selectedItem = null;
          }
        }
        this.refreshContent();
      };
      bg.on('pointerdown', doToggle);
      checkTxt.on('pointerdown', doToggle);
      bg.on('pointerover', () => {
        checkTxt.setColor('#c8973a');
        bg.setStrokeStyle(1.5, 0xc8973a);
        this.itemTooltip?.destroy();
        const tooltipX = Math.min(tx, 420);
        this.itemTooltip = createHudTooltip(this, tooltipX, ty - 14, def.tooltip,
          0.5, 1, { fontSize: '9px', depth: 100 });
      });
      bg.on('pointerout', () => {
        checkTxt.setColor(isOn ? def.color : '#3a2810');
        bg.setStrokeStyle(1.5, isOn ? Phaser.Display.Color.HexStringToColor(def.color).color : 0x2a1808);
        this.itemTooltip?.destroy();
        this.itemTooltip = null;
      });
      checkTxt.on('pointerover', () => bg.emit('pointerover'));
      checkTxt.on('pointerout',  () => bg.emit('pointerout'));
    });
  }

  private drawSigilList(startY: number): void {
    const bag = this.gsm.player.sigilBag ?? [];
    const equipped = new Set(this.gsm.player.sigilsEquipped ?? []);
    const PAGE = 10; // fewer rows because each is 36px tall instead of 26
    const ROW_H = 36;
    this.scrollOffset = Math.min(this.scrollOffset, Math.max(0, bag.length - PAGE));
    const visible = bag.slice(this.scrollOffset, this.scrollOffset + PAGE);

    if (bag.length === 0) {
      const empty = this.add.text(10, startY,
        'No sigils in this run.\nBosses and elites have a small chance to drop one.\nManage your collection at Rudy (main menu).', {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#6b4020', wordWrap: { width: 440 },
        });
      this.contentGroup.add(empty);
      return;
    }

    const slots = this.gsm.effectiveSigilSlots();
    const equippedArr = this.gsm.player.sigilsEquipped ?? [];
    const hint = this.add.text(10, startY - 14, `Run sigils: ${bag.length}   ·   ${equipped.size}/${slots} equipped   ·   Click slot 1 / 2 / 3 to equip.`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020',
    });
    this.contentGroup.add(hint);

    visible.forEach((sigil, i) => {
      const y = startY + i * ROW_H;
      const isEquipped = equipped.has(sigil.id);
      const rowBg = this.add.rectangle(240, y + ROW_H / 2 - 2, 460, ROW_H - 4,
        isEquipped ? 0x1a120a : 0x0d0a08, 0.85,
      ).setStrokeStyle(1, isEquipped ? 0xc0912a : 0x3a2a18).setInteractive({ useHandCursor: true });
      this.contentGroup.add(rowBg);

      if (this.textures.exists(sigil.special.iconKey)) {
        const icon = this.add.image(22, y + ROW_H / 2 - 2, sigil.special.iconKey).setDisplaySize(24, 24);
        this.contentGroup.add(icon);
      }

      // Reserve right-side space for the 3 slot buttons + recycle chip
      // (24x24 each, 4px gap → 4×24 + 3×4 = 108 px).
      const TEXT_RIGHT_PAD = 120;
      const TEXT_LEFT = 42;
      const nameText = this.add.text(TEXT_LEFT, y + 2, sigil.name, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: isEquipped ? '#f0c040' : '#c8973a', fontStyle: 'bold',
      });
      this.contentGroup.add(nameText);

      const affixStr = sigil.affixes.map(formatAffix).join(', ');
      const affixText = this.add.text(TEXT_LEFT, y + 16, affixStr, {
        fontSize: '9px', fontFamily: 'Georgia, serif',
        color: isEquipped ? '#a07840' : '#6b4020',
        wordWrap: { width: 460 - TEXT_LEFT - TEXT_RIGHT_PAD },
      });
      this.contentGroup.add(affixText);

      // Per-slot equip buttons (1 / 2 / 3) + recycle chip — clustered on the
      // right edge of the row. Click slot N to equip into slot N (displaces
      // whatever's there); click ✓ to unequip from that slot. Locked slots
      // (S20 / S40 from Cat Memorial) render dim and reject input. Recycle
      // chip refunds runes; equipped sigils refuse to recycle (recycleSigil
      // returns 'equipped' — guarded server-side too).
      const btnsRightX = 462 - 4; // right edge minus padding
      const btnSize = 24;
      const btnGap = 4;
      const cy = y + ROW_H / 2 - 2;
      // Reverse-x layout: recycle chip is the rightmost (4th button).
      // Slot indices 0/1/2 sit to the LEFT of the recycle chip.
      for (let sIdx = 0; sIdx < 3; sIdx++) {
        const bx = btnsRightX - (3 - sIdx) * (btnSize + btnGap) - btnSize / 2;
        const slotUnlocked = sIdx < slots;
        const thisSlotEq = equippedArr[sIdx] === sigil.id;
        const label = thisSlotEq ? '✓' : String(sIdx + 1);
        const fill = thisSlotEq ? 0x2a1f10 : slotUnlocked ? 0x140e06 : 0x0a0604;
        const stroke = thisSlotEq ? 0xc0912a : slotUnlocked ? 0x4a7c30 : 0x2a1a0a;
        const color = thisSlotEq ? '#f0c040' : slotUnlocked ? '#7acc60' : '#4a3010';
        const bbg = this.add.rectangle(bx, cy, btnSize, btnSize, fill, 0.95)
          .setStrokeStyle(1, stroke).setOrigin(0.5).setInteractive({ useHandCursor: slotUnlocked });
        const btxt = this.add.text(bx, cy, label, {
          fontSize: '11px', fontFamily: 'Georgia, serif', color, fontStyle: 'bold',
        }).setOrigin(0.5).setInteractive({ useHandCursor: slotUnlocked });
        this.contentGroup.add(bbg);
        this.contentGroup.add(btxt);
        if (!slotUnlocked) {
          bbg.setAlpha(0.5);
          btxt.setAlpha(0.5);
          continue;
        }
        const doEquip = () => {
          if (thisSlotEq) {
            this.gsm.unequipSigil(sigil.id);
          } else {
            const current = equippedArr[sIdx];
            if (current) this.gsm.unequipSigil(current);
            if (!this.gsm.equipSigil(sigil.id)) {
              this.showMessage('Cannot equip.');
              return;
            }
          }
          this.refreshContent();
        };
        bbg.on('pointerdown', doEquip);
        btxt.on('pointerdown', doEquip);
      }

      // Recycle chip — rightmost button at bottom-right cluster. Refunds
      // SIGIL_RECYCLE_REFUND runes per scrap. Equipped sigils render dim
      // and reject input (recycleSigil also guards server-side).
      {
        const rx = btnsRightX - btnSize / 2;
        const recyclable = !isEquipped;
        const fillR = 0x140e06;
        const strokeR = recyclable ? 0xd47020 : 0x2a1a0a;
        const colorR = recyclable ? '#ffb060' : '#5a3010';
        const recBg = this.add.rectangle(rx, cy, btnSize, btnSize, fillR, 0.95)
          .setStrokeStyle(1, strokeR).setOrigin(0.5).setInteractive({ useHandCursor: recyclable });
        // Plain text label "REC" — no emoji glyph (the recycling glyph
        // renders as colored emoji on Windows/Chrome and clashes with the
        // bronze palette).
        const recTxt = this.add.text(rx, cy, 'REC', {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: colorR, fontStyle: 'bold',
        }).setOrigin(0.5).setInteractive({ useHandCursor: recyclable });
        this.contentGroup.add(recBg);
        this.contentGroup.add(recTxt);
        if (!recyclable) {
          recBg.setAlpha(0.5);
          recTxt.setAlpha(0.5);
        } else {
          const onRecOver = () => { recBg.setStrokeStyle(1, 0xff8030); recTxt.setColor('#ffd060'); };
          const onRecOut  = () => { recBg.setStrokeStyle(1, strokeR); recTxt.setColor(colorR); };
          recBg.on('pointerover', onRecOver).on('pointerout', onRecOut);
          recTxt.on('pointerover', onRecOver).on('pointerout', onRecOut);
          const doRecycle = () => {
            this.showConfirm(`Recycle ${sigil.name} for ${GameStateManager.SIGIL_RECYCLE_REFUND}◆?`, () => {
              const result = this.gsm.recycleSigil(sigil.id);
              if (result === 'ok') this.showMessage(`Recycled ${sigil.name} (+${GameStateManager.SIGIL_RECYCLE_REFUND}◆)`);
              else if (result === 'equipped') this.showMessage('Unequip first.');
              this.refreshContent();
            });
          };
          recBg.on('pointerdown', doRecycle);
          recTxt.on('pointerdown', doRecycle);
        }
      }

      const showTip = () => {
        this.itemTooltip?.destroy();
        const affixLines = sigil.affixes.map(formatAffix).join('\n');
        const body = [
          sigil.name,
          isEquipped ? '(Equipped)' : '',
          sigil.special.description,
          affixLines ? `— Affixes —\n${affixLines}` : '',
        ].filter(Boolean).join('\n');
        this.itemTooltip = createHudTooltip(this, 10, y + ROW_H, body,
          0, 0, { fontSize: '10px', wrap: 360, depth: 100 });
      };
      const hideTip = () => { this.itemTooltip?.destroy(); this.itemTooltip = null; };
      rowBg.on('pointerover', showTip);
      rowBg.on('pointerout', hideTip);
    });
  }

  private drawItemList(visible: Equipment[], startY: number): void {
    visible.forEach((item, i) => {
      const y = startY + i * 26;
      const color = RARITY_COLORS[item.rarity];
      const isSelected = this.selectedItem?.id === item.id;

      const iconX = 10;
      let iconObj: Phaser.GameObjects.Image | null = null;
      if (item.spriteKey && this.textures.exists(item.spriteKey)) {
        iconObj = this.add.image(iconX + 9, y + 11, item.spriteKey).setDisplaySize(20, 20);
        iconObj.setInteractive({ useHandCursor: true });
        this.contentGroup.add(iconObj);
      }

      const textX = item.spriteKey && this.textures.exists(item.spriteKey) ? 28 : 10;
      const row = this.add.text(textX, y,
        `${isSelected ? '▶' : ' '} ${item.name}${item.rarity === 'unique' ? '' : ` Lv${item.level}`}`,
        {
          fontSize: '11px', fontFamily: 'Georgia, serif', color,
          backgroundColor: isSelected ? '#140a08' : undefined,
          padding: isSelected ? { x: 3, y: 1 } : undefined,
        },
      ).setInteractive({ useHandCursor: true });
      this.contentGroup.add(row);

      const selectItem = () => {
        this.selectedItem = item;
        this.rightScrollOffset = 0;
        this.refreshContent();
      };
      row.on('pointerdown', selectItem);
      iconObj?.on('pointerdown', selectItem);

      const showTooltip = () => {
        this.itemTooltip?.destroy();
        const statLines = formatEquipmentTooltipLines(item);
        const archLabel = (item.armorArchetype || item.armorSubtype)
          ? getArmorTypeLabel(item)
          : (item.weaponType ? getWeaponTypeLabel(item) : '');
        const levelStr = item.rarity === 'unique' ? '' : ` · Lv${item.level}`;
        const isRelic = item.slot.startsWith('relic');
        const effectText = isRelic
          ? (getRelicScaledEffect(item.name, item.level, item.maxStacksRoll) || item.specialEffect || '')
          : (item.specialEffect || '');
        const allStatLines = effectText ? [...statLines, `★ ${effectText}`] : statLines;
        const classLine = isRelic
          ? { text: 'Relic — fits any class', color: '#a07840' }
          : buildEquipStatusAccent(item, this.gsm.player.heroes);
        this.itemTooltip = createItemTooltip(this, 10, y + 18, {
          name: `${item.name}${levelStr}`,
          nameColor: RARITY_COLORS[item.rarity],
          statLines: allStatLines,
          bottomLines: [`Sell: ${item.sellValue}g`],
          classLine,
          typeLabel: archLabel || undefined,
          fontSize: '11px', wrap: 240, depth: 100,
        }, 0, 0);
      };
      const hideTooltip = () => {
        this.itemTooltip?.destroy();
        this.itemTooltip = null;
      };
      row.on('pointerover', showTooltip);
      row.on('pointerout', hideTooltip);
      iconObj?.on('pointerover', showTooltip);
      iconObj?.on('pointerout', hideTooltip);
    });
  }

  private drawItemDetail(item: Equipment): void {
    const y = 308;
    const W = 480;
    const color = RARITY_COLORS[item.rarity];
    const isRelic = item.slot.startsWith('relic');
    const relicSlots: EquipmentSlot[] = ['relic1', 'relic2', 'relic3', 'relic4'];

    // ── Separator ──
    const sep = this.add.rectangle(W / 2, y - 4, W, 1, 0x5a3600, 0.7);
    this.contentGroup.add(sep);

    // ════ LEFT COLUMN — selected item details (x: 10–228) ════
    const lx = 10;
    const colW = 218;

    // Item name
    let nameX = lx;
    if (item.spriteKey && this.textures.exists(item.spriteKey)) {
      const icon = this.add.image(lx + 10, y + 10, item.spriteKey).setDisplaySize(20, 20);
      this.contentGroup.add(icon);
      nameX = lx + 24;
    }
    const levelStr = item.rarity === 'unique' ? '' : ` · Lv${item.level}`;
    const nameT = this.add.text(nameX, y + 1, `${item.name}${levelStr}`, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color, fontStyle: 'bold',
      wordWrap: { width: colW - (nameX - lx) },
    });
    this.contentGroup.add(nameT);

    const classStr = item.classRestriction ? HERO_CLASS_NAMES[item.classRestriction] : '';
    if (classStr) {
      const info = this.add.text(lx, y + 18, classStr, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020',
      });
      this.contentGroup.add(info);
    }

    // Stats
    const statParts: string[] = [];
    for (const [key, val] of Object.entries(item.statBonuses)) {
      if (!val || val === 0) continue;
      const display = key.endsWith('Resist') ? `+${Math.round(val as number)}%` : (typeof val === 'number' ? fmtStatVal(val) : `+${val}`);
      statParts.push(`${display} ${fmtStatKey(key)}`);
    }
    let nextY = y + 31;
    if (statParts.length > 0) {
      const statsLine = this.add.text(lx, nextY, statParts.join('  '), {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#4a8a40',
        wordWrap: { width: colW },
      });
      this.contentGroup.add(statsLine);
      nextY += statsLine.height / 2 + 8;
    }
    // Use the level-scaled passive text for relics so the detail panel matches
    // what HeroScene shows when the same relic is hovered while equipped.
    const detailIsRelic = item.slot.startsWith('relic');
    const detailEffect = detailIsRelic
      ? (getRelicScaledEffect(item.name, item.level, item.maxStacksRoll) || item.specialEffect || '')
      : (item.specialEffect || '');
    if (detailEffect) {
      const fx = this.add.text(lx, nextY, detailEffect, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c87020',
        wordWrap: { width: colW },
      });
      this.contentGroup.add(fx);
      nextY += fx.height / 2 + 6;
    }

    // Sell / Recycle value line
    // Lucky Break boon ("can't recycle items") blocks the recycle button —
    // base rarity check stays so normal rarities still can't recycle.
    const boonBlocksRecycle = aggregateBoonEffects(this.gsm.player.activeBoons).cannotRecycle;
    const canRecycle = (item.rarity === 'epic' || item.rarity === 'legendary' || item.rarity === 'unique')
      && !boonBlocksRecycle;
    const recycleTokens = item.rarity === 'unique' ? 5 : item.rarity === 'legendary' ? 2 : item.rarity === 'epic' ? 1 : 0;
    const recycleLabel = canRecycle ? `  Recycle: ${recycleTokens}◆` : '';
    const sellVal = this.add.text(lx, nextY, `Sell: ${item.sellValue}🪙${recycleLabel}`, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#c8973a',
    });
    this.contentGroup.add(sellVal);
    nextY += 16;

    // Equip buttons — one per eligible hero
    const heroes = this.gsm.player.heroes;
    heroes.forEach((hero, idx) => {
      const canEquip = !item.classRestriction || item.classRestriction === hero.heroClass;
      if (!canEquip) return;
      const wouldBlock = false;

      const ebg = this.add.rectangle(lx + colW / 2, nextY + 9, colW, 18, 0x0d0a08, 1)
        .setStrokeStyle(1, wouldBlock ? 0x3a2800 : 0x5a3600)
        .setInteractive({ useHandCursor: !wouldBlock });
      const etxt = this.add.text(lx + colW / 2, nextY + 9, `▶ Equip → ${hero.name}`, {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: wouldBlock ? '#3a2800' : '#a07840',
      }).setOrigin(0.5).setInteractive({ useHandCursor: !wouldBlock });
      this.contentGroup.add(ebg);
      this.contentGroup.add(etxt);
      nextY += 22;

      if (!wouldBlock) {
        let equipDone = false;
        const doEquip = () => {
          if (equipDone) return; equipDone = true;
          // If relic and all unlocked slots occupied, ask which to replace.
          // Quest-locked relic slots (3/4) are filtered out everywhere.
          if (isRelic) {
            const unlockedRelicSlots = relicSlots.filter((s) =>
              this.gsm.isRelicSlotUnlocked(s as 'relic1' | 'relic2' | 'relic3' | 'relic4'),
            );
            const emptySlot = unlockedRelicSlots.find((s) => !hero.equipment[s]);
            if (!emptySlot) {
              this.showRelicSwapPicker(idx, item, unlockedRelicSlots);
              return;
            }
          }
          this.gsm.equipItem(idx, item);
          this.selectedItem = null;
          this.refreshContent();
        };
        ebg.on('pointerdown', doEquip);
        etxt.on('pointerdown', doEquip);
        ebg.on('pointerover', () => { etxt.setColor('#c8973a'); ebg.setStrokeStyle(1, 0x9a6020); });
        ebg.on('pointerout', () => { etxt.setColor('#a07840'); ebg.setStrokeStyle(1, 0x5a3600); });
      }
    });

    // Sell + Recycle buttons
    const sellBg = this.add.rectangle(lx + 34, nextY + 9, 68, 20, 0x1a0608, 1)
      .setStrokeStyle(1, 0x8b2000).setInteractive({ useHandCursor: true });
    const sellBtn = this.add.text(lx + 34, nextY + 9, 'SELL', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    this.contentGroup.add(sellBg);
    this.contentGroup.add(sellBtn);
    sellBg.on('pointerover', () => { sellBtn.setColor('#ffe8a0'); sellBg.setStrokeStyle(1, 0xc84000); });
    sellBg.on('pointerout', () => { sellBtn.setColor('#e8a030'); sellBg.setStrokeStyle(1, 0x8b2000); });
    const doSell = () => {
      this.showConfirm(`Sell ${item.name} for ${item.sellValue} gold?`, () => {
        const gold = this.gsm.sellItem(item.id);
        this.selectedItem = null;
        if (gold > 0) this.showMessage(`Sold for ${gold} gold`);
        this.refreshContent();
      });
    };
    sellBg.on('pointerdown', doSell);
    sellBtn.on('pointerdown', doSell);

    if (canRecycle) {
      const recBg = this.add.rectangle(lx + 130, nextY + 9, 110, 20, 0x0d0a08, 1)
        .setStrokeStyle(1, 0x1e40af).setInteractive({ useHandCursor: true });
      const recBtn = this.add.text(lx + 130, nextY + 9, `RECYCLE (${recycleTokens}◆)`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'bold',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      this.contentGroup.add(recBg);
      this.contentGroup.add(recBtn);
      recBg.on('pointerover', () => { recBtn.setColor('#c8973a'); recBg.setStrokeStyle(1, 0xb07820); });
      recBg.on('pointerout', () => { recBtn.setColor('#a07840'); recBg.setStrokeStyle(1, 0x6b4010); });
      const doRecycle = () => {
        this.showConfirm(`Recycle ${item.name} for ${recycleTokens} ◆ rune(s)?`, () => {
          const tokens = this.gsm.recycleItem(item.id);
          this.selectedItem = null;
          if (tokens > 0) this.showMessage(`Recycled for ${tokens} ◆`);
          this.refreshContent();
        });
      };
      recBg.on('pointerdown', doRecycle);
      recBtn.on('pointerdown', doRecycle);
    }

    // ════ RIGHT COLUMN — equipped comparison (x: 238–470, scrollable) ════
    const rx = 238;
    const rColW = 232;
    const divX = rx - 6;
    const COMP_TOP = y + 1;
    const COMP_CLIP_H = 576 - COMP_TOP - 8; // clip area height (leaves room for batch-sell bar)

    // Vertical divider (fixed, outside scroll container)
    const vdiv = this.add.rectangle(divX, COMP_TOP + COMP_CLIP_H / 2, 1, COMP_CLIP_H, 0x5a3600, 0.5);
    this.contentGroup.add(vdiv);

    // Scrollable container — all comparison content lives inside it
    const rightCont = this.add.container(0, COMP_TOP - this.rightScrollOffset);
    this.contentGroup.add(rightCont);

    // Geometry mask clips the container to the right column area
    const maskShape = this.make.graphics({});
    maskShape.fillRect(divX + 1, COMP_TOP, rColW + 10, COMP_CLIP_H);
    rightCont.setMask(maskShape.createGeometryMask());

    // Determine which slots to compare
    const compareSlots: EquipmentSlot[] = isRelic ? relicSlots
      : item.slot === 'weapon1' || item.slot === 'weapon2' ? ['weapon1', 'weapon2']
      : [item.slot as EquipmentSlot];

    // compY is LOCAL to the container (starts at 0 = COMP_TOP on screen)
    let compY = 0;

    const compHeader = this.add.text(rx, compY, 'Currently Equipped', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
    });
    rightCont.add(compHeader);
    compY += 16;

    heroes.forEach((hero) => {
      const canEquip = !item.classRestriction || item.classRestriction === hero.heroClass;
      if (!canEquip) return;

      // Hero label
      const heroLbl = this.add.text(rx, compY, hero.name, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840', fontStyle: 'bold',
      });
      rightCont.add(heroLbl);
      compY += 14;

      for (const slot of compareSlots) {
        const equipped = hero.equipment[slot];
        const slotLabel = slot.replace('weapon', 'Wp').replace('relic', 'Rl').replace('armor', 'Arm').replace('reserved', 'Tro');

        if (!equipped) {
          const emptyT = this.add.text(rx + 6, compY, `${slotLabel}: — empty —`, {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: '#3a2800',
          });
          rightCont.add(emptyT);
          compY += 13;
        } else {
          const eqColor = RARITY_COLORS[equipped.rarity] || '#9ca3af';
          const rowX = rx + 6;
          let textStartX = rowX;
          if (equipped.spriteKey && this.textures.exists(equipped.spriteKey)) {
            const eqIcon = this.add.image(rowX + 8, compY + 7, equipped.spriteKey).setDisplaySize(14, 14);
            rightCont.add(eqIcon);
            textStartX = rowX + 18;
          }
          const eqName = this.add.text(textStartX, compY, `${slotLabel}: ${equipped.name}`, {
            fontSize: '9px', fontFamily: 'Georgia, serif', color: eqColor,
            wordWrap: { width: rColW - (textStartX - rx) },
          });
          rightCont.add(eqName);
          compY += eqName.height / 2 + 4;

          // Key stats diff — show selected vs equipped
          const diffParts: string[] = [];
          const allKeys = new Set([
            ...Object.keys(item.statBonuses),
            ...Object.keys(equipped.statBonuses),
          ]);
          for (const k of allKeys) {
            const newV = (item.statBonuses as Record<string, number>)[k] || 0;
            const oldV = (equipped.statBonuses as Record<string, number>)[k] || 0;
            const delta = newV - oldV;
            if (delta === 0) continue;
            const sign = delta > 0 ? '+' : '';
            const absDelta = Math.abs(delta);
            const dispDelta = absDelta < 1
              ? `${sign}${(Math.round(delta * 1000) / 10).toFixed(1)}% ${k}`
              : `${sign}${Math.round(delta * 100) / 100} ${k}`;
            diffParts.push(dispDelta);
          }
          if (diffParts.length > 0) {
            const diffColor = diffParts.every((d) => d.startsWith('+')) ? '#4a8a40'
              : diffParts.every((d) => !d.startsWith('+')) ? '#c84000'
              : '#a07840';
            const diffT = this.add.text(rx + 6, compY, diffParts.join('  '), {
              fontSize: '8px', fontFamily: 'Georgia, serif', color: diffColor,
              wordWrap: { width: rColW - 6 },
            });
            rightCont.add(diffT);
            compY += diffT.height / 2 + 2;
          }
          compY += 4;
        }
      }
      compY += 4;
    });

    // If no heroes can equip, show a note
    if (compY === 16) {
      const noHero = this.add.text(rx, compY, 'No compatible hero', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#3a2800',
      });
      rightCont.add(noHero);
      compY += 13;
    }

    // Update max scroll so wheel handler knows the range
    this.rightScrollMax = Math.max(0, compY - COMP_CLIP_H);

    // Scroll indicator — shown when content overflows
    if (this.rightScrollMax > 0) {
      const scrollHint = this.add.text(rx + rColW - 4, COMP_TOP + 2, '↕', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600',
      }).setOrigin(1, 0);
      this.contentGroup.add(scrollHint);

      // Thin scrollbar track + thumb on the far right
      const trackH = COMP_CLIP_H - 4;
      this.add.rectangle(rx + rColW + 4, COMP_TOP + 2 + trackH / 2, 3, trackH, 0x2a1a0a, 1);
      const thumbH = Math.max(20, trackH * (COMP_CLIP_H / compY));
      const thumbY = COMP_TOP + 2 + (this.rightScrollOffset / this.rightScrollMax) * (trackH - thumbH);
      this.add.rectangle(rx + rColW + 4, thumbY + thumbH / 2, 3, thumbH, 0x8b5a20, 1);
    }
  }

  private getFilteredItems(): Equipment[] {
    let items = this.getSortedItems();
    if (this.filterSlot) {
      if (this.filterSlot === 'weapon1') {
        items = items.filter((i) => i.slot === 'weapon1' || i.slot === 'weapon2');
      } else if (this.filterSlot === 'relic1') {
        items = items.filter((i) => i.slot.startsWith('relic'));
      } else if (this.filterSlot === 'reserved') {
        items = items.filter((i) => i.slot === 'reserved');
      } else {
        items = items.filter((i) => i.slot === this.filterSlot);
      }
    }
    if (this.inventorySearch) {
      items = items.filter((i) => i.name.toLowerCase().includes(this.inventorySearch));
    }
    if (this.statFilters.size > 0) {
      items = items.filter((item) =>
        [...this.statFilters].every((key) => {
          const val = (item.statBonuses as Record<string, number | undefined>)[key];
          return val !== undefined && val > 0;
        })
      );
    }
    return items;
  }

  private getSortedItems(): Equipment[] {
    const items = [...this.gsm.player.inventory];
    const rarityOrder: Record<ItemRarity, number> = { unique: 0, legendary: 1, epic: 2, rare: 3, uncommon: 4, common: 5 };

    switch (this.sortBy) {
      case 'rarity': items.sort((a, b) => rarityOrder[a.rarity] - rarityOrder[b.rarity]); break;
      case 'level':  items.sort((a, b) => b.level - a.level); break;
      case 'slot':   items.sort((a, b) => a.slot.localeCompare(b.slot)); break;
    }
    return items;
  }

  private showRelicSwapPicker(heroIndex: number, item: Equipment, slots: readonly EquipmentSlot[]): void {
    const width = 480; const height = 576;
    const hero = this.gsm.player.heroes[heroIndex];
    const elements: Phaser.GameObjects.GameObject[] = [];

    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.6)
      .setDepth(200).setInteractive();
    elements.push(overlay);

    const popH = 60 + slots.length * 36 + 24;
    const bg = this.add.rectangle(width / 2, height / 2, 340, popH, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0x5a3600).setDepth(201);
    elements.push(bg);

    const title = this.add.text(width / 2, height / 2 - popH / 2 + 18,
      `Replace which relic on ${hero.name}?`, {
        fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold', align: 'center',
      }).setOrigin(0.5).setDepth(202);
    elements.push(title);

    const cleanup = () => elements.forEach((e) => e.destroy());

    let swapDone = false;
    slots.forEach((slot, i) => {
      const equipped = hero.equipment[slot];
      const label = equipped ? equipped.name : '— empty —';
      const slotNum = slot.replace('relic', 'Slot ');
      const rowY = height / 2 - popH / 2 + 50 + i * 36;

      const rbg = this.add.rectangle(width / 2, rowY, 300, 28, 0x120e14, 1)
        .setStrokeStyle(1, 0x5a3600).setDepth(202).setInteractive({ useHandCursor: true });
      const rtxt = this.add.text(width / 2, rowY, `${slotNum}: ${label}`, {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: equipped ? '#a07840' : '#4a4030', align: 'center',
      }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });
      elements.push(rbg, rtxt);

      rbg.on('pointerover', () => { rtxt.setColor('#c8973a'); rbg.setStrokeStyle(1, 0x9a6020); });
      rbg.on('pointerout', () => { rtxt.setColor(equipped ? '#a07840' : '#4a4030'); rbg.setStrokeStyle(1, 0x5a3600); });
      const doSwap = () => {
        if (swapDone) return; swapDone = true;
        cleanup();
        this.gsm.equipItem(heroIndex, item, slot);
        this.selectedItem = null;
        this.refreshContent();
      };
      rbg.on('pointerdown', doSwap);
      rtxt.on('pointerdown', doSwap);
    });

    const cancelY = height / 2 + popH / 2 - 14;
    const cbg = this.add.rectangle(width / 2, cancelY, 100, 22, 0x1a0608, 1)
      .setStrokeStyle(1, 0x8b2000).setDepth(202).setInteractive({ useHandCursor: true });
    const ctxt = this.add.text(width / 2, cancelY, 'CANCEL', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });
    elements.push(cbg, ctxt);
    cbg.on('pointerdown', cleanup);
    ctxt.on('pointerdown', cleanup);
  }

  private showConfirm(message: string, onYes: () => void): void {
    const width = 480; const height = 576;
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.6).setDepth(200).setInteractive();
    const bg = this.add.rectangle(width / 2, height / 2, 320, 120, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0x5a3600).setDepth(201);
    const inner = this.add.rectangle(width / 2, height / 2, 314, 114, 0x0d0a08, 0)
      .setStrokeStyle(1, 0xc8973a, 0.2).setDepth(201);
    const msg = this.add.text(width / 2, height / 2 - 22, message, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840', align: 'center',
      wordWrap: { width: 290 },
    }).setOrigin(0.5).setDepth(202);

    const yesBg = this.add.rectangle(width / 2 - 60, height / 2 + 28, 68, 24, 0x0e1a0e, 1)
      .setStrokeStyle(1, 0x3a7a30).setDepth(202).setInteractive({ useHandCursor: true });
    const yesBtn = this.add.text(width / 2 - 60, height / 2 + 28, 'YES', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#6aaa60', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });
    const noBg = this.add.rectangle(width / 2 + 60, height / 2 + 28, 68, 24, 0x1a0608, 1)
      .setStrokeStyle(1, 0x8b2000).setDepth(202).setInteractive({ useHandCursor: true });
    const noBtn = this.add.text(width / 2 + 60, height / 2 + 28, 'NO', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(203).setInteractive({ useHandCursor: true });

    const cleanup = () => { [overlay, bg, inner, msg, yesBg, yesBtn, noBg, noBtn].forEach((o) => o.destroy()); };
    yesBg.on('pointerdown', () => { cleanup(); onYes(); });
    yesBtn.on('pointerdown', () => { cleanup(); onYes(); });
    noBg.on('pointerdown', () => cleanup());
    noBtn.on('pointerdown', () => cleanup());
  }

  private showMessage(text: string): void {
    const msg = this.add.text(240, 540, text, {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a',
      backgroundColor: '#0d0a0e', padding: { x: 10, y: 5 },
      stroke: '#5a3600', strokeThickness: 1,
    }).setOrigin(0.5).setDepth(100);
    this.tweens.add({ targets: msg, alpha: 0, delay: 1500, duration: 500, onComplete: () => msg.destroy() });
  }

  private closeScene(): void {
    this.searchInput?.destroy();
    this.searchInput = null;
    this.gsm.save();
    this.battleScene.resumeBattle();
    this.scene.stop();
  }
}
